import { BloggerFields } from "@/components/BloggerFields";
import { formatPlace } from "@/lib/districts";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronRight, Copy, Trophy, Crown, Bell, Smartphone, Mail, KeyRound, Eye, LogOut, Trash2, Image as ImageIcon, Briefcase, Send, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { usePublicActiveRoles, RoleBadge, useResidentRoles, useRoleTypes, revokedPeriod } from "@/lib/roles";
import { ExpertServicesSection } from "@/components/ExpertServicesSection";
import { HostsForExpertsSection } from "@/components/ExpertRequestForm";
import { resolveStatus, STATUS_TIERS, useResidentContribution } from "@/lib/drives";
import { arenaRpc } from "@/lib/arena";
import { ARENA_FIRST_MEETING_DRIVES, ARENA_MAX_REWARDED_MEETINGS, ARENA_NEXT_MEETING_DRIVES } from "@/lib/arena-config";
import { buildInviteLink } from "@/lib/referral";
import { pluralCityLabel, pluralCoffeeMeetingLabel, pluralHeldMeetingLabel, pluralHostedCircleLabel, pluralPeopleCameLabel, pluralVisitedMeetingLabel } from "@/lib/plural";
import { profileCoreProgress, profileFullProgress, type CoreProgressKey } from "@/lib/profile-progress";
import { normalizeTimeInTurkey, type Profile } from "@/lib/people-constants";
import { normalizeTopics } from "@/lib/event-topics";
import { socialLabel } from "@/lib/social-links";
import { isTelegramLike, normalizeTelegram } from "@/lib/coffee-contact";
import { ResidentProfileView } from "@/components/ResidentProfileView";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { PushSettingsCard } from "@/components/PushSettingsCard";
import { EmailSettingsCard } from "@/components/EmailSettingsCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BasicProfileFields, ContactProfileFields, InterestsProfileFields, MoreProfileFields, ProfilePhotoField, type ProfileEditState } from "@/components/ProfileEditFields";
import { FieldError, ProfileEditDrawer, highlightField } from "@/components/ProfileEditDrawer";
import { normalizeLookingFor } from "@/lib/looking-for";
import { PURPOSES, isPurposeSelected } from "@/lib/profile-purpose";
import type { Database } from "@/integrations/supabase/types";
import { useServerFn } from "@tanstack/react-start";
import { countPendingDeletions } from "@/lib/account-deletion.functions";

type ProfileUpdate = Database["public"]["Tables"]["profiles"]["Update"];
type EditSection = "photo" | "basic" | "contacts" | "interests" | "more";

export const Route = createFileRoute("/_authenticated/profile")({
  head: () => ({ meta: [
    { title: "Мой профиль — TurkeyDrive" },
    { name: "description", content: "Профиль резидента TurkeyDrive: данные, достижения и настройки." },
    { property: "og:title", content: "Мой профиль — TurkeyDrive" },
    { property: "og:description", content: "Профиль резидента TurkeyDrive: данные, достижения и настройки." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  validateSearch: (search: Record<string, unknown>): { section?: string } => typeof search.section === "string" ? { section: search.section } : {},
  loader: async ({ context }) => { await context.queryClient.ensureQueryData({ queryKey: ["profile", context.user.id], queryFn: async () => { const { data, error } = await supabase.from("profiles").select("*").eq("user_id", context.user.id).single(); if (error) throw error; return data as Profile; } }); },
  errorComponent: ({ error }) => <div className="min-h-screen bg-white px-4 py-12 text-center"><h1 className="text-xl font-semibold text-red-600">Ошибка загрузки профиля</h1><p className="mt-2 text-sm text-td-muted">{(error as Error).message}</p></div>,
  notFoundComponent: () => <div className="min-h-screen bg-white px-4 py-12 text-center">Профиль не найден</div>,
  component: ProfilePage,
});

function stateFromProfile(profile: Profile): ProfileEditState {
  const extended = profile as Profile & { event_topics?: string[] | null };
  return {
    full_name: profile.full_name ?? "", photo_url: profile.photo_url ?? "", city: profile.city === "Не указан" ? "" : profile.city ?? "", district: profile.district ?? "", neighborhood: profile.neighborhood ?? "",
    visitor_kind: profile.visitor_kind ?? "resident", time_in_turkey: normalizeTimeInTurkey(profile.time_in_turkey), origin_city: profile.origin_city ?? "",
    languages: profile.languages ?? [], profession: profile.profession ?? "", industries: profile.industries?.length ? profile.industries : profile.industry ? [profile.industry] : [],
    age: profile.age ? String(profile.age) : "", gender: profile.gender ?? "", telegram: profile.telegram ?? "", social_links: (profile.social_links ?? []).filter(Boolean), is_blogger: !!profile.is_blogger, blog_url: profile.blog_url ?? "",
    group_coffee_opt_in: !!profile.group_coffee_opt_in, group_coffee_slots: profile.group_coffee_slots ?? [], looking_for_tags: profile.looking_for_tags ?? [], looking_for: profile.looking_for ?? "", coffee_status: profile.coffee_status ?? [], partner_gender_pref: profile.partner_gender_pref ?? "",
    partner_age_from: profile.partner_age_from ? String(profile.partner_age_from) : "", partner_age_to: profile.partner_age_to ? String(profile.partner_age_to) : "",
    interests: profile.interests ?? [], event_topics: normalizeTopics(extended.event_topics), about: profile.about ?? "", conversation_topic: profile.conversation_topic ?? "",
  };
}

function ProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { section } = Route.useSearch();
  const { data: profile } = useSuspenseQuery({ queryKey: ["profile", user?.id], queryFn: async () => { if (!user) throw new Error("Нужно войти"); const { data, error } = await supabase.from("profiles").select("*").eq("user_id", user.id).single(); if (error) throw error; return data as Profile; } });
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [editSection, setEditSection] = useState<EditSection | null>(null);
  const [draft, setDraft] = useState<ProfileEditState>(() => stateFromProfile(profile));
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const showWelcome = !profile.has_seen_welcome && !welcomeDismissed;
  const progress = profileCoreProgress(profile);
  const fullProgress = profileFullProgress(profile);

  useEffect(() => { setDraft(stateFromProfile(profile)); }, [profile]);
  useEffect(() => {
    if (!section || !["basic", "contacts", "interests", "more", "services", "notifications"].includes(section)) return;
    const timer = window.setTimeout(() => {
      document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (section !== "services" && section !== "notifications") setEditSection(section as EditSection);
    }, 100);
    return () => window.clearTimeout(timer);
  }, [section]);
  useEffect(() => {
    if (profile.has_seen_welcome || !user?.id) return;
    void supabase.from("profiles").update({ has_seen_welcome: true }).eq("user_id", user.id).then(() => queryClient.invalidateQueries({ queryKey: ["profile", user.id] }));
  }, [profile.has_seen_welcome, queryClient, user?.id]);

  const save = useMutation({
    mutationFn: async () => {
      if (!user || !editSection) return;
      const numberOrNull = (value: string) => value.trim() ? Number(value) : null;
      let patch: ProfileUpdate = {};
      if (editSection === "photo") patch = { photo_url: draft.photo_url || null };
      if (editSection === "basic") {
        const name = draft.full_name.trim();
        if (!name) throw new FieldError("Укажи имя", "full_name");
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name) || /@[^\s@]+\.[^\s@]+/.test(name)) throw new FieldError("Вместо email укажи имя", "full_name");
        const age = numberOrNull(draft.age);
        if (age !== null && (!Number.isFinite(age) || age < 18 || age > 120)) throw new FieldError("Возраст должен быть от 18 до 120", "age");
      }
      if (editSection === "basic") patch = { full_name: draft.full_name.trim(), photo_url: draft.photo_url || null, ...(draft.city.trim() ? { city: draft.city.trim(), district: draft.district?.trim() || null, neighborhood: draft.district?.trim() ? draft.neighborhood?.trim() || null : null } : {}), visitor_kind: draft.visitor_kind, time_in_turkey: draft.time_in_turkey || null, origin_city: draft.origin_city.trim() || null, languages: draft.languages, profession: draft.profession.trim() || null, industry: draft.industries[0] ?? null, industries: draft.industries, age: numberOrNull(draft.age), gender: draft.gender || null };
      let tg: string | null = null;
      if (editSection === "contacts" && draft.telegram.trim()) {
        if (!isTelegramLike(draft.telegram.trim())) throw new FieldError("Похоже, это не Telegram. Впиши @username или добавь ссылку", "telegram");
        tg = normalizeTelegram(draft.telegram.trim());
      }
      if (editSection === "contacts" && draft.is_blogger && !draft.blog_url?.trim()) throw new FieldError("Добавь ссылку на блог", "blog_url");
      if (editSection === "contacts") patch = { is_blogger: !!draft.is_blogger, blog_url: draft.is_blogger ? draft.blog_url?.trim() || null : null, telegram: tg, social_links: draft.social_links.map((item) => item.trim()).filter(Boolean), looking_for_tags: draft.looking_for_tags, looking_for: normalizeLookingFor(draft.looking_for), coffee_status: draft.coffee_status, group_coffee_opt_in: !!draft.group_coffee_opt_in, group_coffee_slots: draft.group_coffee_slots ?? [], partner_gender_pref: draft.partner_gender_pref || null, partner_age_from: numberOrNull(draft.partner_age_from), partner_age_to: numberOrNull(draft.partner_age_to) };
      if (editSection === "interests") { if (draft.interests.length < 3) throw new FieldError("Отметь минимум 3 интереса", "interests"); patch = { interests: draft.interests }; }
      if (editSection === "more") patch = { about: draft.about.trim() || null, conversation_topic: draft.conversation_topic.trim() || null };
      const { error } = await supabase.from("profiles").update(patch).eq("user_id", user.id); if (error) throw error;
    },
    onSuccess: async () => { toast.success("Сохранено"); setEditSection(null); await queryClient.invalidateQueries({ queryKey: ["profile", user?.id] }); await queryClient.invalidateQueries({ queryKey: ["people"] }); },
    onError: (error: Error) => { toast.error(error.message); if (error instanceof FieldError) highlightField(error.field); },
  });

  async function signOut() { await supabase.auth.signOut(); navigate({ to: "/", replace: true }); }
  const open = (target: EditSection) => { setDraft(stateFromProfile(profile)); setEditSection(target); };
  const missingTarget: Record<CoreProgressKey, EditSection> = { photo: "photo", city: "basic", contact: "contacts", interests: "interests", about: "basic" };

  return <div className="min-h-screen bg-white"><main className="mx-auto max-w-3xl px-4 py-8 md:py-12">
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="mb-6 h-auto w-full justify-start gap-1 bg-td-soft/60 p-1"><TabsTrigger value="profile" className="data-[state=active]:bg-td-teal data-[state=active]:text-white">Профиль</TabsTrigger><TabsTrigger value="achievements" className="data-[state=active]:bg-td-teal data-[state=active]:text-white">Достижения</TabsTrigger><TabsTrigger value="settings" className="data-[state=active]:bg-td-teal data-[state=active]:text-white">Настройки</TabsTrigger></TabsList>
      <TabsContent value="profile" className="mt-0">
        <h1 className="mb-3 font-display text-xl font-bold text-td-dark">Как тебя видят другие</h1>
        <ProfileSummary profile={profile} onEdit={open} onPreview={() => setPreviewOpen(true)} onShowAchievements={() => setActiveTab("achievements")} />
        {showWelcome && <WelcomeCard onDismiss={() => setWelcomeDismissed(true)} />}
        <div className="mb-7 mt-4">
          {progress.done === progress.total ? <p className="text-sm font-medium text-td-teal">Основа профиля готова ✓</p> : <><p className="text-sm font-semibold text-td-dark">Основа профиля: {progress.done} из {progress.total}</p><div className="mt-2 flex flex-wrap gap-2">{progress.missing.map((item) => <Button key={item.key} type="button" size="sm" variant="outline" onClick={() => open(missingTarget[item.key])}>+ {item.label}</Button>)}</div></>}
          <div className="mt-3">{fullProgress.done === fullProgress.total ? <p className="text-sm font-medium text-td-teal">Профиль заполнен полностью ✓</p> : <><p className="text-sm text-td-muted">Полный профиль: {fullProgress.done} из {fullProgress.total}</p><div className="mt-2 flex flex-wrap gap-2">{fullProgress.missing.map((item) => <Button key={item.key} type="button" size="sm" variant="outline" onClick={() => open(item.section)}>+ {item.label}</Button>)}</div></>}</div>
        </div>
        <h2 className="mb-3 font-display text-xl font-bold text-td-dark">Обо мне</h2>
        <div className="space-y-3">
          <ProfileSection id="basic" title="Основное" summary={basicSummary(profile)} onEdit={() => open("basic")} />
          <ProfileSection id="contacts" title="Контакты и знакомства" summary={contactSummary(profile)} onEdit={() => open("contacts")} />
          <ProfileSection id="interests" title="Интересы" summary={(profile.interests?.length ?? 0) ? (profile.interests ?? []).join(" · ") : "Интересы пока не выбраны"} onEdit={() => open("interests")} />
          <ProfileSection id="more" title="Расскажи о себе больше" summary={[profile.about, profile.conversation_topic].filter(Boolean).join(" · ") || "Можно добавить рассказ о себе и тему для разговора"} onEdit={() => open("more")} />
        </div>
        <div id="services" className="mt-7 scroll-mt-24"><ExpertServicesSection profile={profile} onOpenContacts={() => open("contacts")} /></div>
        <div id="hosts-for-experts" className="mt-4 scroll-mt-24"><HostsForExpertsSection uid={profile.user_id} /></div>
      </TabsContent>
      <TabsContent value="achievements" className="mt-0"><ResidentPassport profile={profile} /><Card className="mb-6 border-td-teal/30 bg-primary-50"><CardContent className="pt-6"><div className="flex flex-col items-center gap-3"><MrDriveAvatar variant="drives" size={168} /><h3 className="text-center text-lg font-semibold text-td-dark">Привет, я Mr. Drive <span aria-hidden>🐾</span></h3><div className="w-full space-y-3 text-sm leading-relaxed text-td-muted">
  <p>TurkeyDrive создают сами участники. Здесь ты находишь своих людей, помогаешь другим получать ответы на важные вопросы, делишься опытом и полезными контактами, создаёшь мероприятия и становишься частью живого сообщества.</p>
  <p>Каждое такое действие помогает кому-то быстрее освоиться, почувствовать поддержку и сделать Турцию своим домом.</p>
  <p>За полезные действия ты получаешь драйвы — показатель твоего вклада в сообщество.</p>
  <p>Чем больше драйвов, тем выше твой статус:{" "}<span className="font-medium text-td-dark">Yabancı → Vatandaş → Sultan</span></p>
  <p>С каждым статусом для тебя открываются новые возможности. Драйвы нельзя купить — их можно только заработать, принося пользу другим.</p>
</div>
<div className="w-full rounded-2xl border border-td-teal/20 bg-white px-4 py-3 text-sm font-medium text-td-dark">Не гонись за драйвами. Просто будь полезен — драйвы придут сами🧡</div></div></CardContent></Card><DrivesWaysCard userId={profile.user_id} /></TabsContent>
      <TabsContent value="settings" className="mt-0"><SettingsTab profile={profile} userEmail={user?.email ?? ""} onSignOut={signOut} /></TabsContent>
    </Tabs>
    <EditDrawer section={editSection} draft={draft} setDraft={setDraft} userId={profile.user_id} saving={save.isPending} onClose={() => setEditSection(null)} onSave={() => save.mutate()} />
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}><DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"><DialogHeader><DialogTitle>Так тебя видят другие</DialogTitle></DialogHeader><ResidentProfileView profile={profile} /></DialogContent></Dialog>
  </main></div>;
}

function ProfileSummary({ profile, onEdit, onPreview, onShowAchievements }: { profile: Profile; onEdit: (section: EditSection) => void; onPreview: () => void; onShowAchievements: () => void }) {
  const roles = usePublicActiveRoles(profile.user_id);
  const { data: contribution } = useResidentContribution(profile.user_id);
  const drives = contribution?.drives ?? 0;
  const manualOverride = (profile as unknown as { manual_status_override?: string | null }).manual_status_override ?? null;
  const tier = resolveStatus(drives, manualOverride);
  const contact = profile.telegram?.trim() || (profile.social_links ?? []).find(Boolean) || "";
  return <Card className="border-td-line"><CardContent className="pt-6">
    <div className="flex items-start gap-4"><button type="button" onClick={() => onEdit("photo")} className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-td-line bg-td-soft text-center text-xs font-semibold text-td-teal">{profile.photo_url ? <img loading="lazy" decoding="async" src={profile.photo_url} alt={profile.full_name} className="size-full object-cover" /> : "+ Добавить фото"}</button><div className="min-w-0 flex-1"><button type="button" onClick={() => onEdit("basic")} className="block text-left"><h2 className="font-display text-xl font-bold text-td-dark">{profile.full_name}</h2><p className="mt-1 text-sm text-td-muted">{[formatPlace(profile.city, profile.district, profile.neighborhood) || null, normalizeTimeInTurkey(profile.time_in_turkey)].filter(Boolean).join(" · ") || "+ Добавить город"}</p></button><div className="mt-2 flex flex-wrap gap-1.5">{roles.map((role, index) => <RoleBadge key={`${role.role_key}-${index}`} role={role} tone="light" size="sm" />)}</div></div></div>
    <button type="button" onClick={onShowAchievements} className="mt-3 inline-flex max-w-full items-center rounded-full px-3 py-1.5 text-xs font-bold text-white shadow-sm" style={{ backgroundImage: `linear-gradient(135deg, ${tier.gradientFrom}, ${tier.gradientTo})` }}><span className="truncate">{tier.label.toUpperCase()} · {drives} драйвов →</span></button>
    <div className="mt-5 grid gap-2"><SummaryButton icon={<Briefcase className="size-4" />} text={profile.profession || "+ Чем занимаешься"} onClick={() => onEdit("basic")} /><SummaryButton icon={<Send className="size-4" />} text={contact ? socialLabel(contact) : "+ Добавить Telegram"} onClick={() => onEdit("contacts")} /></div>
    <Button type="button" variant="outline" className="mt-4 w-full" onClick={onPreview}>Посмотреть, как видят другие</Button>
  </CardContent></Card>;
}
function SummaryButton({ icon, text, onClick }: { icon: React.ReactNode; text: string; onClick: () => void }) { return <Button type="button" variant="ghost" onClick={onClick} className="h-auto justify-start gap-2 px-2 py-2 text-left text-sm text-td-dark">{icon}<span className="truncate">{text}</span><Pencil className="ml-auto size-3.5 text-td-muted" /></Button>; }
function ProfileSection({ id, title, summary, onEdit }: { id: string; title: string; summary: string; onEdit: () => void }) { return <section id={id} className="scroll-mt-24 rounded-lg border border-td-line bg-white p-4"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><h3 className="font-semibold text-td-dark">{title}</h3><p className="mt-1 line-clamp-3 text-sm leading-relaxed text-td-muted">{summary}</p></div><Button type="button" variant="ghost" size="sm" onClick={onEdit}>Изменить</Button></div></section>; }
function basicSummary(p: Profile) { return [formatPlace(p.city, p.district, p.neighborhood), normalizeTimeInTurkey(p.time_in_turkey), p.profession, ...(p.industries ?? []), ...(p.languages ?? [])].filter(Boolean).join(" · ") || "Основные данные пока не заполнены"; }
function contactSummary(p: Profile) {
  const rawContact = p.telegram?.trim() || (p.social_links ?? []).find((item) => item?.trim())?.trim() || "";
  const contact = p.telegram?.trim() ? `@${p.telegram.trim().replace(/^@/, "")}` : rawContact ? socialLabel(rawContact) : "";
  const state = { tags: p.looking_for_tags ?? [], statuses: p.coffee_status ?? [] };
  const purposeLabels = PURPOSES.filter((purpose) => isPurposeSelected(purpose.key, state)).map((purpose) => purpose.label);
  return [...new Set([contact, ...purposeLabels].filter(Boolean))].join(" · ") || "Контакты и цели знакомства пока не указаны";
}

function EditDrawer({ section, draft, setDraft, userId, saving, onClose, onSave }: { section: EditSection | null; draft: ProfileEditState; setDraft: (value: ProfileEditState) => void; userId: string; saving: boolean; onClose: () => void; onSave: () => void }) {
  const titles: Record<EditSection, string> = { photo: "Фото", basic: "Основное", contacts: "Контакты и знакомства", interests: "Интересы", more: "Расскажи о себе больше" };
  return <ProfileEditDrawer open={section !== null} title={section ? titles[section] : "Редактирование"} saving={saving} onOpenChange={(open) => !open && onClose()} onSave={onSave}>{section === "photo" && <ProfilePhotoField large userId={userId} value={draft.photo_url} fullName={draft.full_name} onChange={(photo_url) => setDraft({ ...draft, photo_url })} />}{section === "basic" && <BasicProfileFields value={draft} onChange={setDraft} userId={userId} />}{section === "contacts" && <><ContactProfileFields value={draft} onChange={setDraft} /><BloggerFields className="mt-6" isBlogger={!!draft.is_blogger} blogUrl={draft.blog_url ?? ""} onChange={(v) => setDraft({ ...draft, is_blogger: v.isBlogger, blog_url: v.blogUrl })} /></>}{section === "interests" && <InterestsProfileFields value={draft} onChange={setDraft} />}{section === "more" && <MoreProfileFields value={draft} onChange={setDraft} />}</ProfileEditDrawer>;
}

function WelcomeCard({ onDismiss }: { onDismiss: () => void }) { return <Card className="mt-4 border-td-teal/30 bg-primary-50"><CardContent className="pt-6"><h3 className="font-semibold text-td-dark">Добро пожаловать в TurkeyDrive!</h3><p className="mt-2 text-sm text-td-muted">Заполни профиль, найди своих людей и пригласи кого-нибудь на кофе.</p><Button type="button" className="mt-3" onClick={onDismiss}>Понятно</Button></CardContent></Card>; }

function SettingsTab({
  profile,
  userEmail,
  onSignOut,
}: {
  profile: Profile;
  userEmail: string;
  onSignOut: () => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState<boolean>(profile.visible_in_directory ?? true);
  const [savingVisible, setSavingVisible] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleVisibleChange(next: boolean) {
    setVisible(next);
    setSavingVisible(true);
    const { error } = await supabase
      .from("profiles")
      .update({ visible_in_directory: next })
      .eq("user_id", profile.user_id);
    setSavingVisible(false);
    if (error) {
      setVisible(!next);
      toast.error(error.message);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["profile", profile.user_id] });
    queryClient.invalidateQueries({ queryKey: ["people"] });
    toast.success(next ? "Твой профиль виден в разделе «Люди»" : "Профиль скрыт из раздела «Люди»");
  }

  async function handleResetPassword() {
    if (!userEmail) {
      toast.error("Email не найден");
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Письмо со ссылкой отправлено на почту");
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    const { error } = await supabase
      .from("profiles")
      .update({ deletion_requested_at: new Date().toISOString(), deletion_processed_at: null, deletion_notified_at: null, visible_in_directory: false })
      .eq("user_id", profile.user_id);
    setDeleting(false);
    setConfirmDelete(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Запрос принят. Профиль уже скрыт. Мы напишем тебе, когда данные будут удалены.");
    await onSignOut();
  }

  return (
    <div className="space-y-6 pb-10">
      {/* Аккаунт */}
      <section>
        <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-td-muted">Аккаунт</h3>
        <div className="overflow-hidden rounded-2xl border border-td-line bg-white">
          <div className="flex items-center gap-3 px-4 py-3">
            <Mail className="h-5 w-5 text-td-muted" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-td-muted">Email</div>
              <div className="truncate text-sm text-td-dark">{userEmail || "—"}</div>
            </div>
          </div>
          <div className="h-px bg-td-line" />
          <button
            type="button"
            onClick={handleResetPassword}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-td-soft/50"
          >
            <KeyRound className="h-5 w-5 text-td-muted" aria-hidden="true" />
            <span className="flex-1 text-sm text-td-dark">Сменить пароль</span>
            <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* Приватность */}
      <section>
        <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-td-muted">Приватность</h3>
        <div className="overflow-hidden rounded-2xl border border-td-line bg-white">
          <div className="flex items-center gap-3 px-4 py-3">
            <Eye className="h-5 w-5 text-td-muted" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-td-dark">Показываться в разделе «Люди»</div>
              <div className="text-xs text-td-muted">
                Когда выключено, другие участники не увидят тебя в общем списке.
              </div>
            </div>
            <Switch
              checked={visible}
              onCheckedChange={handleVisibleChange}
              disabled={savingVisible}
              aria-label="Показываться в разделе «Люди»"
            />
          </div>
        </div>
      </section>

      {/* Админка (только для админов) */}
      {(profile as unknown as { is_admin?: boolean }).is_admin ? (
        <section>
          <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-td-muted">Админка</h3>
          <div className="overflow-hidden rounded-2xl border border-td-line bg-white">
            <Link
              to="/admin/residents"
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-td-soft/50"
            >
              <Trophy className="h-5 w-5 text-td-muted" aria-hidden="true" />
              <span className="flex-1 text-sm text-td-dark">Статусы резидентов</span>
              <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
            </Link>
            <Link
              to="/admin/deletions"
              className="flex w-full items-center gap-3 border-t border-td-line px-4 py-3 text-left transition hover:bg-td-soft/50"
            >
              <Trash2 className="h-5 w-5 text-td-muted" aria-hidden="true" />
              <span className="flex-1 text-sm text-td-dark">Запросы на удаление</span>
              <PendingDeletionsBadge />
              <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
            </Link>
            <Link
              to="/admin/roles"
              className="flex w-full items-center gap-3 border-t border-td-line px-4 py-3 text-left transition hover:bg-td-soft/50"
            >
              <Crown className="h-5 w-5 text-td-muted" aria-hidden="true" />
              <span className="flex-1 text-sm text-td-dark">Роли и титулы</span>
              <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
            </Link>
            <Link
              to="/admin/coffee-groups"
              className="flex w-full items-center gap-3 border-t border-td-line px-4 py-3 text-left transition hover:bg-td-soft/50"
            >
              <Bell className="h-5 w-5 text-td-muted" aria-hidden="true" />
              <span className="flex-1 text-sm text-td-dark">Кофе на районе</span>
              <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
            </Link>
            <Link
              to="/admin/push"
              className="flex w-full items-center gap-3 border-t border-td-line px-4 py-3 text-left transition hover:bg-td-soft/50"
            >
              <Bell className="h-5 w-5 text-td-muted" aria-hidden="true" />
              <span className="flex-1 text-sm text-td-dark">Отправить сообщение</span>
              <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
            </Link>
            <Link
              to="/admin/arena"
              className="flex w-full items-center gap-3 border-t border-td-line px-4 py-3 text-left transition hover:bg-td-soft/50"
            >
              <Trophy className="h-5 w-5 text-td-muted" aria-hidden="true" />
              <span className="flex-1 text-sm text-td-dark">ARENA</span>
              <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
            </Link>
            <Link
              to="/admin/install"
              className="flex w-full items-center gap-3 border-t border-td-line px-4 py-3 text-left transition hover:bg-td-soft/50"
            >
              <Smartphone className="h-5 w-5 text-td-muted" aria-hidden="true" />
              <span className="flex-1 text-sm text-td-dark">Приложение на устройствах</span>
              <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
            </Link>
            <Link
              to="/admin/banners"
              className="flex w-full items-center gap-3 border-t border-td-line px-4 py-3 text-left transition hover:bg-td-soft/50"
            >
              <ImageIcon className="h-5 w-5 text-td-muted" aria-hidden="true" />
              <span className="flex-1 text-sm text-td-dark">Баннеры главной</span>
              <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
            </Link>
          </div>
        </section>
      ) : null}

      <div id="notifications" className="flex scroll-mt-24 flex-col gap-3">
        <PushSettingsCard />
        <EmailSettingsCard />
      </div>

      {/* Опасная зона */}
      <section>
        <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-td-muted">Опасная зона</h3>
        <div className="overflow-hidden rounded-2xl border border-td-line bg-white">
          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-td-soft/50"
          >
            <LogOut className="h-5 w-5 text-td-muted" aria-hidden="true" />
            <span className="flex-1 text-sm text-td-dark">Выйти</span>
            <ChevronRight className="h-4 w-4 text-td-muted" aria-hidden="true" />
          </button>
          <div className="h-px bg-td-line" />
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-red-50"
          >
            <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
            <span className="flex-1 text-sm font-medium text-red-600">Удалить аккаунт</span>
            <ChevronRight className="h-4 w-4 text-red-400" aria-hidden="true" />
          </button>
        </div>
      </section>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить аккаунт?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить — профиль и все твои данные будут удалены. Мы получим твой запрос и завершим удаление вручную в течение нескольких дней. Ты будешь сразу разлогинен(а), а профиль скроется из раздела «Люди».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-600/90"
            >
              {deleting ? "Удаляем…" : "Да, удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function ResidentPassport({ profile }: { profile: Profile }) {
  const queryClient = useQueryClient();
  const { data: c, isSuccess: contributionLoaded } = useResidentContribution(profile.user_id);
  const drives = c?.drives ?? 0;
  const manualOverride = (profile as unknown as { manual_status_override?: string | null }).manual_status_override ?? null;
  const tier = resolveStatus(drives, manualOverride);
  const tierIndex = STATUS_TIERS.findIndex((t) => t.key === tier.key);
  const nextTier = tierIndex < STATUS_TIERS.length - 1 ? STATUS_TIERS[tierIndex + 1] : null;
  const progress = nextTier
    ? Math.min(100, Math.max(0, Math.round(((drives - tier.min) / (nextTier.min - tier.min)) * 100)))
    : 100;
  const since = formatMemberSince(profile.created_at);
  const invitePath = profile.username ? `/${profile.username}/invite` : "";
  const fullLink = profile.username ? buildInviteLink(profile.username) : "";
  const lastSeen = (profile as unknown as { last_seen_status?: string | null }).last_seen_status ?? null;

  useEffect(() => {
    if (!profile.user_id || !contributionLoaded) return;
    if (lastSeen === tier.key) return;
    const isInitialDefault = lastSeen === null && tier.key === "yabanci";
    if (!isInitialDefault) {
      toast(`Теперь ты ${tier.label}!`, { icon: "🐾" });
    }
    supabase
      .from("profiles")
      .update({ last_seen_status: tier.key })
      .eq("user_id", profile.user_id)
      .then(({ error }) => {
        if (error) {
          toast.error(error.message);
          return;
        }
        queryClient.invalidateQueries({ queryKey: ["profile", profile.user_id] });
      });
  }, [contributionLoaded, lastSeen, profile.user_id, queryClient, tier.key, tier.label]);

  async function copyInvite() {
    if (!fullLink) return;
    try {
      await navigator.clipboard.writeText(fullLink);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать");
    }
  }

  const { data: circles = [] } = useQuery({
    queryKey: ["passport-circles", profile.user_id],
    queryFn: async () => {
      const [mem, hosted] = await Promise.all([
        supabase
          .from("circle_members")
          .select("circle_id, circles(id, name, logo_url, status, is_deleted, host_user_id)")
          .eq("user_id", profile.user_id),
        supabase
          .from("circles")
          .select("id, name, logo_url, status, is_deleted, host_user_id")
          .eq("host_user_id", profile.user_id),
      ]);
      if (mem.error) throw mem.error;
      if (hosted.error) throw hosted.error;
      type C = { id: string; name: string; logo_url: string | null; status: string; is_deleted: boolean; host_user_id: string | null };
      const map = new Map<string, C & { hosting: boolean }>();
      const add = (x: C | null | undefined) => {
        if (!x || x.is_deleted || x.status !== "published") return;
        const hosting = x.host_user_id === profile.user_id;
        const prev = map.get(x.id);
        map.set(x.id, { ...x, hosting: hosting || !!prev?.hosting });
      };
      for (const r of (mem.data ?? []) as unknown as Array<{ circles: C | null }>) add(r.circles);
      for (const x of (hosted.data ?? []) as C[]) add(x);
      return [...map.values()].sort((a, b) => Number(b.hosting) - Number(a.hosting));
    },
  });

  const metrics = [
    { label: "человек приглашено", value: c?.referrals ?? 0 },
    { label: pluralCoffeeMeetingLabel(c?.coffee_confirmed ?? 0), value: c?.coffee_confirmed ?? 0 },
    { label: pluralHeldMeetingLabel(c?.events_held ?? 0), value: c?.events_held ?? 0 },
    { label: pluralPeopleCameLabel(c?.people_came ?? 0), value: c?.people_came ?? 0 },
    { label: pluralVisitedMeetingLabel(c?.event_attendance ?? 0), value: c?.event_attendance ?? 0 },
    { label: pluralHostedCircleLabel(c?.circles_hosted ?? 0), value: c?.circles_hosted ?? 0 },
    { label: pluralCityLabel(c?.cities ?? 0), value: c?.cities ?? 0 },
  ].filter((m) => m.value > 0);
  const { data: allRoles = [] } = useResidentRoles(profile.user_id);
  const { data: roleTypes = [] } = useRoleTypes();
  const publicMap = new Map(roleTypes.map((t) => [t.key, t.is_public]));
  const visibleRoles = allRoles.filter((r) => publicMap.get(r.role_key) !== false);
  const activeRoles = visibleRoles.filter((r) => r.is_active && r.kind !== "title" && r.duration !== "one_time");
  const titleRoles = visibleRoles.filter((r) => r.is_active && r.kind === "title");
  const oneTimeRoles = visibleRoles.filter((r) => r.is_active && r.kind !== "title" && r.duration === "one_time");
  const pastRoles = visibleRoles.filter((r) => !r.is_active);
  const hostedCircles = circles.filter((circle) => circle.hosting);
  const joinedCircles = circles.filter((circle) => !circle.hosting);

  const initials = (profile.full_name ?? "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className="mb-6 max-w-md overflow-hidden rounded-2xl p-5 text-white shadow-lg"
      style={{ backgroundImage: `linear-gradient(135deg, ${tier.gradientFrom}, ${tier.gradientTo})` }}
    >
      <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">Мой путь</div>
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {profile.photo_url ? (
            <img loading="lazy" decoding="async" src={profile.photo_url} alt={profile.full_name} className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-white/30" />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20 text-sm font-semibold ring-2 ring-white/30">
              {initials}
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate font-display text-lg font-semibold text-white">{profile.full_name}</div>
            {since && <div className="mt-0.5 text-[11px] text-white/70">в сообществе с {since}</div>}
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-black/25 px-3 py-1 text-[10px] font-semibold tracking-wider text-white">
          {tier.label.toUpperCase()}
        </span>
      </div>

      <div className="mt-6">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-white/70">Вклад</div>
        <div className="flex items-baseline gap-2">
          <span className="mt-2 font-display text-4xl font-bold leading-none text-white">{drives}</span>
          <span className="text-sm text-white/80">драйвов 🔥</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/25">
          <div className="h-full rounded-full bg-white" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-1.5 text-[12px] text-white/80">
          {nextTier ? `Ещё ${Math.max(0, nextTier.min - drives)} драйвов до статуса ${nextTier.label}` : "Максимальный статус достигнут"}
        </p>

        {metrics.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-3">
            {metrics.map((m) => (
              <div key={m.label} className="min-w-0">
                <div className="font-display text-2xl font-bold leading-none text-white">{m.value}</div>
                <div className="mt-1 text-[11px] leading-tight text-white/70">{m.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {activeRoles.length > 0 && <PassportRoleGroup label="Роли" roles={activeRoles} />}
      {titleRoles.length > 0 && <PassportRoleGroup label="Титулы" roles={titleRoles} />}
      {oneTimeRoles.length > 0 && <PassportRoleGroup label="Разовое" roles={oneTimeRoles} />}
      {pastRoles.length > 0 && (
        <div className="mt-6 min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/70">Было раньше</div>
          <ul className="mt-2 space-y-1 text-[13px] text-white/85">
            {pastRoles.map((r, i) => (
              <li key={`${r.role_key}-${i}`} className="break-words">{r.icon} {r.title}{r.project_title ? ` «${r.project_title}»` : ""}{revokedPeriod(r) ? <span className="text-white/60"> · {revokedPeriod(r)}</span> : null}</li>
            ))}
          </ul>
        </div>
      )}

      {circles.length > 0 && (
        <div className="mt-6 min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/70">Сообщества</div>
          {hostedCircles.length > 0 && <PassportCircleGroup label="Веду" circles={hostedCircles} />}
          {joinedCircles.length > 0 && <PassportCircleGroup label="Участвую" circles={joinedCircles} />}
        </div>
      )}

      {invitePath && (
        <div className="mt-6">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-white/70">Приглашение</div>
          <Button
            type="button"
            variant="ghost"
            onClick={copyInvite}
            className="mt-2 flex h-auto w-full min-w-0 items-center justify-between gap-2 rounded-lg bg-black/20 px-3 py-2 text-left text-[13px] text-white hover:bg-black/30 hover:text-white"
            title="Скопировать ссылку-приглашение"
          >
            <span className="min-w-0 truncate font-mono">{invitePath}</span>
            <Copy className="h-3.5 w-3.5 shrink-0 text-white/70" />
          </Button>
        </div>
      )}

      <div className="mt-6 border-t border-white/20 pt-3 text-[11px] text-white/70">
        TurkeyDrive · сообщество русскоязычных в Турции
      </div>
    </div>
  );
}

function PassportRoleGroup({ label, roles }: { label: string; roles: ReturnType<typeof useResidentRoles>["data"] & object }) {
  return (
    <div className="mt-6 min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-white/70">{label}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {roles.map((r, i) => <RoleBadge key={`${r.role_key}-${i}`} role={r} tone="dark" />)}
      </div>
    </div>
  );
}

function formatMemberSince(date: string | null | undefined): string {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const months = [
    "января",
    "февраля",
    "марта",
    "апреля",
    "мая",
    "июня",
    "июля",
    "августа",
    "сентября",
    "октября",
    "ноября",
    "декабря",
  ];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

function PassportCircleGroup({
  label,
  circles,
}: {
  label: string;
  circles: Array<{ id: string; name: string; logo_url: string | null }>;
}) {
  return (
    <div className="mt-3 min-w-0">
      <div className="text-[10px] text-white/50">{label}</div>
      <div className="mt-1.5 flex min-w-0 flex-wrap gap-2">
        {circles.map((circle) => (
          <Link
            key={circle.id}
            to="/circles/$circleId"
            params={{ circleId: circle.id }}
            className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full bg-white/15 py-1 pl-1 pr-3 text-[12px] text-white transition hover:bg-white/25 hover:text-white hover:underline"
          >
            {circle.logo_url ? (
              <img loading="lazy" decoding="async" src={circle.logo_url} alt="" className="h-5 w-5 shrink-0 rounded-full object-cover" />
            ) : (
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/25 text-[10px] font-semibold text-white">
                {circle.name.slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="min-w-0 truncate text-white">{circle.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}


function DrivesWaysCard({ userId }: { userId: string }) {
  const { data: c } = useResidentContribution(userId);
  const { data: arenaMeetings = 0 } = useQuery({
    queryKey: ["arena-rewarded", userId],
    queryFn: async () => {
      const { data } = await arenaRpc.rewarded(userId);
      return (data as number | null) ?? 0;
    },
  });

  type Way = {
    label: string;
    price: number;
    count?: number;
    done?: boolean;
    isBool?: boolean;
    hint?: string;
  };

  const groups: { title: string; items: Way[] }[] = [
    {
      title: "Знакомства и связи",
      items: [
        {
          label: "Состоявшаяся встреча на кофе",
          price: 10,
          count: c?.coffee_confirmed ?? 0,
          hint: "Засчитываются только состоявшиеся встречи",
        },
        ...(arenaMeetings > 0
          ? [
              {
                label: `Встреча на ARENA (${ARENA_FIRST_MEETING_DRIVES} за первую, по ${ARENA_NEXT_MEETING_DRIVES} за следующие, до ${ARENA_MAX_REWARDED_MEETINGS})`,
                price: ARENA_FIRST_MEETING_DRIVES,
                count: arenaMeetings,
                hint: "Засчитывается, когда встречу подтвердили оба",
              },
            ]
          : []),
      ],
    },
    {
      title: "Мероприятия и сообщества",
      items: [
        {
          label: "Организовать мероприятие (организатор или соорганизатор)",
          price: 20,
          count: c?.events_held ?? 0,
        },
        {
          label: "Вести опубликованное сообщество",
          price: 20,
          count: c?.circles_hosted ?? 0,
        },
        {
          label: "Записаться на мероприятие",
          price: 5,
          count: c?.event_attendance ?? 0,
        },
      ],
    },
    {
      title: "Развитие сообщества",
      items: [
        {
          label: "Пригласить друга по личной ссылке",
          price: 5,
          count: c?.referrals ?? 0,
        },
        {
          label:
            "Быстрый старт (заполнить профиль + заглянуть в «Люди» + первое приглашение)",
          price: 20,
          isBool: true,
          done: c?.quick_start === true,
        },
      ],
    },
  ];

  return (
    <Card className="mb-6 border-td-line">
      <CardContent className="pt-6">
        <h3 className="mb-4 text-lg font-semibold text-td-dark">
          Как получать драйвы
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g.title}>
              <div className="mb-2 text-sm font-semibold text-td-dark">
                {g.title}
              </div>
              <ul className="space-y-2 text-sm text-td-muted">
                {g.items.map((w) => (
                  <li
                    key={w.label}
                    className={`space-y-0.5 ${w.isBool ? "" : (w.count ?? 0) === 0 ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span>{w.label}</span>
                      <span className="shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-td-teal">
                        +{w.price} 🔥
                      </span>
                    </div>
                    {w.isBool ? (
                      w.done ? (
                        <div className="text-[11px] text-td-muted">получено ✅</div>
                      ) : null
                    ) : (w.count ?? 0) > 0 ? (
                      <div className="text-[11px] text-td-muted">
                        у тебя {w.count} · {(w.count ?? 0) * w.price} 🔥
                      </div>
                    ) : null}
                    {w.hint ? (
                      <div className="text-[11px] leading-snug text-td-muted/70">
                        {w.hint}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-td-muted">
          Драйвы начисляются автоматически после выполнения действия. Для
          некоторых действий может потребоваться подтверждение.
        </p>
      </CardContent>
    </Card>
  );
}


function PendingDeletionsBadge() {
  const fetchCount = useServerFn(countPendingDeletions);
  const { data } = useQuery({ queryKey: ["admin", "deletions-count"], queryFn: () => fetchCount() });
  if (!data?.count) return null;
  return (
    <span className="rounded-full bg-td-teal px-2 py-0.5 text-xs font-semibold text-primary-foreground">
      {data.count}
    </span>
  );
}
